/**
 * Strategic Context Selector Component
 * 
 * Provides cascading selectors for the 5-level strategic framework:
 * Level 1: Strategic Objective → Level 2: Strategic Initiative → Level 3: Operational Objective
 * 
 * All selections are OPTIONAL and used for strategic context/reporting only.
 * 
 * Usage:
 * <StrategicContextSelector
 *   value={{
 *     strategic_objective_id: 1,
 *     strategic_initiative_id: 2,
 *     operational_initiative_id: 3
 *   }}
 *   onChange={(context) => setContext(context)}
 * />
 */

import { useEffect } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { 
  useLookupStrategicObjectives,
  useLookupStrategicInitiatives,
  useLookupOperationalInitiatives
} from '@/hooks/useStrategicFramework'

export interface StrategicContext {
  strategic_objective_id?: number | null
  strategic_initiative_id?: number | null
  operational_initiative_id?: number | null
}

interface StrategicContextSelectorProps {
  value?: StrategicContext
  onChange?: (context: StrategicContext) => void
  disabled?: boolean
  compact?: boolean
  showLabels?: boolean
}

export function StrategicContextSelector({
  value = {},
  onChange,
  disabled = false,
  compact = false,
  showLabels = true,
}: StrategicContextSelectorProps) {
  const { strategic_objective_id, strategic_initiative_id, operational_initiative_id } = value

  // Fetch dropdown data
  const { data: objectives = [], isLoading: loadingObjectives } = useLookupStrategicObjectives()
  const { data: initiatives = [], isLoading: loadingInitiatives } = useLookupStrategicInitiatives(
    strategic_objective_id || undefined
  )
  const { data: operationalInitiatives = [], isLoading: loadingOperational } = useLookupOperationalInitiatives(
    strategic_initiative_id || undefined
  )

  // Reset dependent selections when parent changes
  useEffect(() => {
    // Skip validation if initiatives are still loading (prevents clearing AI-applied complete paths)
    if (loadingInitiatives) {
      return
    }
    
    if (strategic_initiative_id && strategic_objective_id && initiatives.length > 0) {
      // Check if the selected initiative still belongs to the selected objective
      const initiative = initiatives.find(i => i.id === strategic_initiative_id)
      if (!initiative || initiative.strategic_objective_id !== strategic_objective_id) {
        // Reset if initiative doesn't match
        onChange?.({
          strategic_objective_id,
          strategic_initiative_id: null,
          operational_initiative_id: null,
        })
      }
    }
  }, [strategic_objective_id, initiatives, strategic_initiative_id, operational_initiative_id, onChange, loadingInitiatives])

  const handleObjectiveChange = (value: string) => {
    const objectiveId = value === 'none' ? null : parseInt(value)
    onChange?.({
      strategic_objective_id: objectiveId,
      strategic_initiative_id: null,
      operational_initiative_id: null,
    })
  }

  const handleInitiativeChange = (value: string) => {
    const initiativeId = value === 'none' ? null : parseInt(value)
    onChange?.({
      strategic_objective_id,
      strategic_initiative_id: initiativeId,
      operational_initiative_id: null,
    })
  }

  const handleOperationalChange = (value: string) => {
    const operationalId = value === 'none' ? null : parseInt(value)
    onChange?.({
      strategic_objective_id,
      strategic_initiative_id,
      operational_initiative_id: operationalId,
    })
  }

  const selectedObjective = objectives.find(o => o.id === strategic_objective_id)

  return (
    <div className="space-y-3">
      {showLabels && (
        <div className="flex items-center gap-2">
          <Label className="text-sm font-semibold">Strategic Context (Optional)</Label>
          <InfoTooltip text="Align this objective with Eduvos strategic objectives (Impact, Culture, Quality, Scale) and their cascaded initiatives" />
        </div>
      )}

      <div className={compact ? "space-y-2" : "space-y-3"}>
        {/* Level 1: Strategic Objective */}
        <div>
          <Label className="text-xs text-gray-600">Level 1: Strategic Objective</Label>
          <Select
            value={strategic_objective_id?.toString() || 'none'}
            onValueChange={handleObjectiveChange}
            disabled={disabled || loadingObjectives}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select strategic objective (optional)">
                {selectedObjective ? (
                  <div className="flex items-center gap-2">
                    {selectedObjective.color_code && (
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: selectedObjective.color_code }}
                      />
                    )}
                    <span>{selectedObjective.code}. {selectedObjective.display_name}</span>
                  </div>
                ) : (
                  'None'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-gray-500">None</span>
              </SelectItem>
              {objectives.map(obj => (
                <SelectItem key={obj.id} value={obj.id.toString()}>
                  <div className="flex items-center gap-2">
                    {obj.color_code && (
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: obj.color_code }}
                      />
                    )}
                    <span>{obj.code}. {obj.display_name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Level 2: Strategic Initiative */}
        <div>
          <Label className="text-xs text-gray-600">Level 2: Strategic Initiative</Label>
          <Select
            value={strategic_initiative_id?.toString() || 'none'}
            onValueChange={handleInitiativeChange}
            disabled={disabled || !strategic_objective_id || loadingInitiatives}
          >
            <SelectTrigger>
              <SelectValue placeholder={strategic_objective_id ? "Select initiative (optional)" : "Select Level 1 first"}>
                {strategic_initiative_id
                  ? initiatives.find(i => i.id === strategic_initiative_id)?.code + ' ' + initiatives.find(i => i.id === strategic_initiative_id)?.name
                  : 'None'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-gray-500">None</span>
              </SelectItem>
              {initiatives.map(init => (
                <SelectItem key={init.id} value={init.id.toString()}>
                  {init.code} {init.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Level 3: Operational Objective */}
        <div>
          <Label className="text-xs text-gray-600">Level 3: Operational Objective</Label>
          <Select
            value={operational_initiative_id?.toString() || 'none'}
            onValueChange={handleOperationalChange}
            disabled={disabled || !strategic_initiative_id || loadingOperational}
          >
            <SelectTrigger>
              <SelectValue placeholder={strategic_initiative_id ? "Select operational objective (optional)" : "Select Level 2 first"}>
                {operational_initiative_id
                  ? operationalInitiatives.find(o => o.id === operational_initiative_id)?.code + ' ' + operationalInitiatives.find(o => o.id === operational_initiative_id)?.name
                  : 'None'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-gray-500">None</span>
              </SelectItem>
              {operationalInitiatives.map(op => (
                <SelectItem key={op.id} value={op.id.toString()}>
                  <div className="flex flex-col">
                    <span className="font-medium">{op.code}</span>
                    <span className="text-xs text-gray-500">{op.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Show current selection summary if any selected */}
      {(strategic_objective_id || strategic_initiative_id || operational_initiative_id) && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {strategic_objective_id && selectedObjective && (
            <Badge
              style={{ backgroundColor: selectedObjective.color_code || '#6B7280' }}
              className="text-white"
            >
              L1: {selectedObjective.code}. {selectedObjective.display_name}
            </Badge>
          )}
          {strategic_initiative_id && initiatives.find(i => i.id === strategic_initiative_id) && (
            <Badge variant="outline">
              L2: {initiatives.find(i => i.id === strategic_initiative_id)?.code}
            </Badge>
          )}
          {operational_initiative_id && operationalInitiatives.find(o => o.id === operational_initiative_id) && (
            <Badge variant="outline">
              L3: {operationalInitiatives.find(o => o.id === operational_initiative_id)?.code}
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Display-only version showing strategic context badges
 */
interface StrategicContextDisplayProps {
  context: StrategicContext
  objectives?: Array<{ id: number; code: string; display_name: string; color_code?: string }>
  initiatives?: Array<{ id: number; code: string; name: string }>
  operationalInitiatives?: Array<{ id: number; code: string; name: string }>
}

export function StrategicContextDisplay({
  context,
  objectives,
  initiatives,
  operationalInitiatives,
}: StrategicContextDisplayProps) {
  const selectedObjective = objectives?.find(o => o.id === context.strategic_objective_id)
  const selectedInitiative = initiatives?.find(i => i.id === context.strategic_initiative_id)
  const selectedOperational = operationalInitiatives?.find(o => o.id === context.operational_initiative_id)

  if (!context.strategic_objective_id && !context.strategic_initiative_id && !context.operational_initiative_id) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-2">
      {selectedObjective && (
        <Badge
          style={{ backgroundColor: selectedObjective.color_code || '#6B7280' }}
          className="text-white"
        >
          {selectedObjective.code}. {selectedObjective.display_name}
        </Badge>
      )}
      {selectedInitiative && (
        <Badge variant="outline">
          {selectedInitiative.code} {selectedInitiative.name}
        </Badge>
      )}
      {selectedOperational && (
        <Badge variant="outline" className="text-xs">
          {selectedOperational.code}
        </Badge>
      )}
    </div>
  )
}
